/*
 * Inspired by Haskell's Either implementation.
 *
 * Original reason for implementation:
 * Needed a way to represent a validation that could be in one of two states:
 *   Success or Not Success
 *
 * Accordingly, the Either type fits this purpose exactly and does not immediately require a `throw`.
 */

import { Functor } from "./functor";

/**
 * The Either type can exist in one of two states: Left or Right.
 */
export enum EitherType {
  Left,
  Right,
}

/**
 * The Either type represents values with two possibilities.
 * It can either be Left (traditionally used for errors) or Right.
 * These values are encoded in the types.
 *
 * Source Reference: http://hackage.haskell.org/package/base-4.12.0.0/docs/src/Data.Either.html
 */
export class Either<TLeft, TRight> implements Functor<TRight> {

  /**
   * Produces a Left instance with the given value.
   * @param value The value to associate with the created instance.
   */
  public static left<TLeft, TRight>(value: TLeft): Either<TLeft, TRight> {
    return new Either<TLeft, TRight>(EitherType.Left, value);
  }

  /**
   * Produces a Right instance with the given value.
   * @param value The value to associate with the created instance.
   */
  public static right<TLeft, TRight>(value: TRight): Either<TLeft, TRight> {
    return new Either<TLeft, TRight>(EitherType.Right, value);
  }
  private constructor(
    public readonly type: EitherType,
    public readonly value: TLeft | TRight,
  ) {
  }

  /**
   * Determines and returns whether the current Either instance is considered Left.
   */
  public isLeft(): boolean {
    return this.type === EitherType.Left;
  }

  /**
   * Determines and returns whether the current Either instance is considered Right.
   */
  public isRight(): boolean {
    return this.type === EitherType.Right;
  }

  /**
   * Returns the contents of a Left value or the default value otherwise.
   * @param defaultValue The default value to ues if the current Either instance is not a Left value.
   */
  public fromLeft(defaultValue: TLeft): TLeft {
    return this.isLeft() ? this.value as TLeft : defaultValue;
  }

  /**
   * Returns the contents of a Right value or the default value otherwise.
   * @param defaultValue The default value to use if the current Either instance is not a Right value.
   */
  public fromRight(defaultValue: TRight): TRight {
    return this.isRight() ? this.value as TRight : defaultValue;
  }

  /** @inheritDoc */
  public map<B>(f: (a: TRight) => B): Either<TLeft, B> {
    if (this.isLeft()) {
      return Either.left<TLeft, B>(this.value as TLeft);
    } else {
      return Either.right<TLeft, B>(f(this.value as TRight));
    }
  }

  /** Produces a string representation of the Either instance. */
  public toString(): string {
    return `${this.type === EitherType.Left ? "Left" : "Right"} (${this.value})`;
  }
}
